//TODO: Show alert if more than one row is edited since not supported in backend yet.
import React, { useRef, useEffect, useMemo, ReactElement } from "react";
import AreaUpdateDto from "../../../Models/Area/AreaUpdateDto";
import useKendoTableState from "../../Shared/Hooks/useKendoTableState";
import AreaReadTableDto from "../../../Models/Area/AreaReadTableDto";
import { ColumnMetaData } from "../../Shared/Interfaces/ColumnMetaData";
import GBKendoDataCell from "../../Shared/Components/Table/GBKendoDataCell";
import { CompositeFilterDescriptor, filterBy, orderBy } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridFilterChangeEvent, GridPageChangeEvent, GridSortChangeEvent, GridToolbar, GridColumn as Column, GridRowProps } from "@progress/kendo-react-grid";
import { isRequiredBySchema } from "../../Shared/Utilities/YupValidationUtilities";
import AreaValidationSchema from "../Utilities/AreaValidationSchema";
import { Formik } from "formik";
import GBKendoToolbar from "../../Shared/Components/Table/GBKendoToolbar";
import { CustomDateFilterCell } from "../../Shared/Components/Table/CustomDateFilterCell";
import { CustomTextFilterCell } from "../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useToast } from "@chakra-ui/react";
import areasApiService from "../Utilities/AreasApiService";
import EntityNotesButton from "../../Dashboards/Assets/Components/Notes/EntityNotesButton";

interface Props {
  areas: Array<AreaReadTableDto>;
  createArea: (values: any, setStatus: any) => void;
  deleteAreas: (deleteIds: Array<string>) => void;
  updateAreas: (areas: Array<AreaUpdateDto>) => void;
  height: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

function AreaTable(props: any) {
  const tableState = useKendoTableState<AreaReadTableDto>({
    key: "Areas",
    initialAssets: props.areas,
  });

  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const localizationService = useLocalization();

  const stateRef = useRef(tableState);

  useEffect(() => {
    stateRef.current = tableState
   }, [tableState]);

  const [filter, setFilter] = React.useState(initialFilter);
  const [sort, setSort] = React.useState(props.initialSortDescriptor ?? props.initialSort);
  const [previousSort, setPreviousSort] = React.useState(sort || props.initialSortDescriptor || props.initialSort);
  const [editModeRows, setEditModeRows] = React.useState<Array<any>>([]);

  // Table columns
  const columns: Array<ColumnMetaData<AreaReadTableDto>> = React.useMemo(
      () =>
        [
          {
            header: `${localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}`,
            displayField: "description",
            filterType: "boolean",
            Cell: (cellProps) => (
              <GBKendoDataCell<AreaReadTableDto>
                className={"text-center"}
                dataFieldName="description"
                cellProps={cellProps}
                fieldAccessor={(x) => (
                  <EntityNotesButton
                    entityType="Location"
                    entityId={x.id}
                    entityName={x.name}
                    buttonProps={{ fillMode: "flat" }}
                  />
                )}
                isAddMode={false}
                isEditMode={false}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.name', enMessages.custom.name)}`,
            displayField: "name",
            filterType: "text",
            Cell: (cellProps) => (
              <GBKendoDataCell<AreaReadTableDto>
                dataFieldName="name"
                cellProps={cellProps}
                fieldAccessor={(x) => x.name}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
              />
            ),
          },
          {
            header: `${localizationService.toLanguageString('custom.parentLocation', enMessages.custom.parentLocation)}`,
            displayField: "parentName",
            filterType: "text",
            Cell: (cellProps) => (
              <GBKendoDataCell<AreaReadTableDto>
                dataFieldName="parentId"
                fieldAccessor={(x) => x.parentId}
                displayTextAccessor={(x) => x.name}
                cellProps={cellProps}
                isAddMode={cellProps.dataIndex < tableState.tempRows.length}
                isEditMode={tableState.canModifyRow}
                onCellChanged={tableState.onCellChanged}
                assets={tableState.assets}
                inputType="combobox"
                initialDisplayText={cellProps.dataItem.parentName}
              />
            ),
          },
        ] as Array<ColumnMetaData<AreaReadTableDto>>,
        [tableState.canModifyRow, tableState.tempRows.length, filter, sort]
  );

  const _export = React.useRef<ExcelExport | null>(null);
  // Notes are managed outside this table's query, so latestNote in the cache can be stale.
  // On export, fetch fresh areas directly from the API (bypassing react-query so the page's
  // isFetching loader doesn't unmount this table), patch the fresh latestNote onto the
  // already-processed rows, then export that data.
  const excelExport = async () => {
    let exportData = processedData;
    try {
      const fresh = (await areasApiService.getAllAreas()) as Array<{ id: string; latestNote?: string | null }>;
      const latestNoteById = new Map(fresh.map((a) => [a.id, a.latestNote]));
      exportData = processedData.map((row) =>
        latestNoteById.has(row.id) ? { ...row, latestNote: latestNoteById.get(row.id) } : row
      );
    } catch (e) {
      console.log(e);
    }
    _export.current?.save(exportData);
  };
  const ref = useRef<any>();

  /**The following use effects are required to fix a race condition created by updating formik fields
   * Programatically. They ensure that validation is performed after the values update instead of before.
   **/
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current?.values]);
  useEffect(() => {
    ref.current.validateForm();
  }, [ref.current]);

  /**
   * Set the skip amount to zero if the number of temp rows (indicating an add row has been added).
   * This effectively ensures the add row is always scrolled into view
   */
  useEffect(() => {
    if (tableState.tempRows.length === 1) {
      setSkip(0);
    }
  }, [tableState.tempRows.length]);

  //The following state is required for dynamic scrolling
  const [skip, setSkip] = React.useState<number>(0);
  const pageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
  };

  const isColumnForRequiredField = (displayField: string | undefined): boolean => {
    return (
      isRequiredBySchema(displayField?.replace("Name", "Id"), AreaValidationSchema) &&
      (tableState.tempRows.length > 0 || tableState.canModifyRow)
    );
  };

  useEffect(() => {
    // Reset skip when filter or sort changes
    setSkip(0);
  }, [filter, sort]);

  // Add this effect to handle edit mode row storage
  useEffect(() => {
    if (tableState.canModifyRow) {
        // Store current sorted rows when entering edit mode
        if (editModeRows.length === 0) {
            const sortedRows = sort ? orderBy(tableState.assets, sort) : tableState.assets;
            setEditModeRows(sortedRows);
        }
    } else {
        setEditModeRows([]);
    }
  }, [tableState.canModifyRow]);

  // Replace the existing processedData implementation
  const processedData = useMemo(() => {
    let initialRows = tableState.assets;  // Always start with tableState.assets

    if (filter) {
      // First get the modified rows
      const modifiedRows = initialRows.filter(row => tableState.modifiedRowIds.includes(row.id));
      
      // Filter the non-modified rows
      const filteredRows = filterBy(
        initialRows.filter(row => !tableState.modifiedRowIds.includes(row.id)),
        filter
      );

      // Combine both sets
      initialRows = [...modifiedRows, ...filteredRows];
    }

    // Use stored order in edit mode, otherwise normal sorting
    if (tableState.canModifyRow && editModeRows.length > 0) {
      const editModeOrder = new Map(editModeRows.map((row, index) => [row.id, index]));
      initialRows.sort((a, b) => (editModeOrder.get(a.id) ?? 0) - (editModeOrder.get(b.id) ?? 0));
    } else if (sort) {
      initialRows = orderBy(initialRows, sort);
    }

    return tableState.tempRows.concat(initialRows);
  }, [tableState.assets, tableState.tempRows, tableState.modifiedRowIds, filter, sort]);

  //Initial row added
  const initialisedRowData = {
    description: "",
    id: "",
    photoUrl: "",
    siteId: "",
    status: "Active",
    name: "",
    parentId: "",
    parentName: "",
    expanded: true
  };

  return (
    <div>
      <Formik
        innerRef={ref}
        key={props.areas.length}
        onSubmit={async (values, { setStatus, resetForm }) => {
          if (tableState.tempRows.length > 0) {
            values.description = tableState.tempRows[0].description;
          }

          // Check for duplicate area name in the same parent
          const parentId = values.parentId === "" ? null : values.parentId;
          const duplicate = props.areas.find(area => 
            area.name.toLowerCase() === values.name.toLowerCase() && 
            (!area.parentId || area.parentId === "") === (!parentId || parentId === "")
          );

          if (duplicate) {
            toast({
              status: "error",
              title: localizationService.toLanguageString('custom.error', enMessages.custom.error),
              description: parentId 
                ? localizationService.toLanguageString('custom.name', enMessages.custom.locationExists)
                : localizationService.toLanguageString('custom.name', enMessages.custom.uniqueName),
            });
            return;
          }

          const res = await props.createArea(values, setStatus);
          if (res) {
            resetForm();
            tableState.setTempRows([]);
          }
        }}
        validationSchema={AreaValidationSchema}
        initialValues={initialisedRowData}
      >
        {({ submitForm, values, resetForm, isSubmitting }) => (
          <>
            <ExcelExport data={processedData} ref={_export} fileName={localizationService.toLanguageString('custom.locations', enMessages.custom.locations)} >
              <ExcelExportColumn field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} />
              <ExcelExportColumn field="parentName" title={localizationService.toLanguageString('custom.parentLocation', enMessages.custom.parentLocation)} />
              <ExcelExportColumn field="latestNote" title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)} />
            </ExcelExport>
            <Grid
              key={tableState.tempRows.length.toString()}
              rowHeight={46}
              pageSize={30}
              total={processedData.length}
              data={processedData}
              scrollable="virtual"
              skip={skip}
              onPageChange={pageChange}
              filterable={true}
              filter={filter}
              onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
              sortable={!tableState.canModifyRow}
              sort={tableState.canModifyRow ? previousSort : sort}
              onSortChange={(e: GridSortChangeEvent) => {
                if (!tableState.canModifyRow) {
                    setSort(e.sort);
                    setPreviousSort(e.sort);
                }
              }}
              style={{
                height: props.height,
              }}
              className="w-full  z-0"
              //Custom render row,
              rowRender={(trElement: ReactElement, props: GridRowProps) => {
                let baseStyle = { ...trElement.props.style };
                if (tableState.modifiedRowIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 252, 88, 0.25)";
                }
                if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                  baseStyle.backgroundColor = "rgba(255, 99, 88, 0.25)";
                }

                return React.cloneElement(
                  trElement,
                  {
                    className: trElement.props.className,
                    style: baseStyle,
                    onClick: trElement.props.onClick,
                    onDoubleClick: trElement.props.onDoubleClick,
                    onContextMenu: trElement.props.onContextMenu,
                    role: trElement.props.role,
                    'aria-rowindex': trElement.props['aria-rowindex'],
                    'data-grid-row-index': trElement.props['data-grid-row-index']
                  },
                  trElement.props.children
                );
              }}
            >
              <GridToolbar>  
                <GBKendoToolbar
                  name={localizationService.toLanguageString('custom.locations', enMessages.custom.locations)}
                  tableState={tableState}
                  exportAction={excelExport}
                  importAction={null}
                  addAction={
                    props.createArea
                      ? () => {
                        if (tableState.tempRows.length === 1) {
                          tableState.setTempRows([]);
                          return;
                        }
                        tableState.setTempRows([...tableState.tempRows, initialisedRowData]);
                        }
                      : null
                  }
                  updateAction={
                    props.updateAreas
                      ? async () => {
                          let updatedAssets = tableState.assets.filter((x) => tableState.modifiedRowIds.includes(x.id));
                          await props.updateAreas(updatedAssets);
                        }
                      : null
                  }
                  deleteAction={
                    props.deleteAreas ? async () => await props.deleteAreas(tableState.selectedRowsIds) : null
                  }
                  discardChanges={() => tableState.resetState()}
                />
              </GridToolbar>

              {/* Selection - only if in edit mode*/}
              {tableState.canModifyRow && (
                <Column  
                  key={"Selection"}
                  cell={(props) => (
                    <td>
                      
                        <Checkbox
                          onChange={() => {
                            if (tableState.selectedRowsIds.includes(props.dataItem.id)) {
                              tableState.setSelectedRowIds(
                                tableState.selectedRowsIds.filter((id) => id !== props.dataItem?.id)
                              );
                            } else {
                              tableState.setSelectedRowIds([...tableState.selectedRowsIds, props.dataItem?.id]);
                            }
                          }}
                          checked={tableState.selectedRowsIds.includes(props.dataItem?.id)}
                        />
                    </td>
                  )}
                  width="50px"
                  filterable={false}
                  sortable={false} />)}

               {/* From Column Array */}
               {columns.map((column, index) => {
                 if(column.displayField === "description") {
                  return (
                    //Create wrapper/HOC with editable cell and add cell definitions,
                    <Column
                    width="65px"
                    className="text-center"
                    key={index}
                    cell={column.Cell}
                    title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                    field={column.displayField}
                    filterable={false}
                    sortable={true}
                    />
                  );
                } else {
                  return (
                    //Create wrapper/HOC with editable cell and add cell definitions,
                    <Column
                      width="auto"
                      key={index}
                      cell={column.Cell}
                      // Show asterix in header if required TODO: pretty hacky - assumes that the name of the data field is the same as the display field with Name replaced.
                      title={`${column.header}${isColumnForRequiredField(column.displayField) ? "*" : ""}`}
                      field={column.displayField}
                      filter={column.filterType}
                      filterCell={column.filterType === "date" ? CustomDateFilterCell : CustomTextFilterCell}
                    />
                  );
                }
              })}


              {/* Inline Action options (If ActionDefinitions are provided) */}
              {props.extraInlineActions != null && props.extraInlineActions.length > 0 && (
                <Column
                  title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
                  sortable={false}
                  filterable={false}
                  cell={(cellProps) => {
                    return (
                      <td className="flex flex-wrap justify-around gap-1">
                        {tableState.tempRows.length <= cellProps.dataIndex &&
                          props.extraInlineActions.map((action: any) => {
                            return (
                              <Button
                                className=""
                                themeColor={action.themeColor}
                                onClick={() => action.onClick(cellProps.dataItem)}
                              >
                                {action.text}
                              </Button>
                            );
                          })}
                      </td>
                    );
                  }}
                />
              )}

              {/* Command Cell (For add rows only)*/}
              {tableState.tempRows.length > 0 && (
                <Column
                  width="150px"
                  sortable={false}
                  filterable={false}
                  key={"Commands"}
                  cell={(props) => {
                    // Add Button for new assets
                    return (
                      <td>
                        {props.dataIndex === 0 && (
                          <div className="flex space-x-1">
                            <Button onClick={submitForm} disabled={isSubmitting} themeColor={"primary"}>
                            {localizationService.toLanguageString('custom.add', enMessages.custom.add)}
                            </Button>
                            <Button
                              onClick={() => {
                                tableState.setTempRows([]);
                                resetForm();
                              }}
                              themeColor={"base"}
                            >
                              {localizationService.toLanguageString('custom.exit', enMessages.custom.exit)}
                            </Button>
                          </div>
                        )}
                      </td>
                    );
                  }}
                />
              )}
            </Grid>
          </>
          )}
      </Formik>
    </div>
  );
};

export default AreaTable;